import { useState } from "react";

import clsx from "clsx";
import { format, isEqual, parseISO } from "date-fns";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { PrimaryButton, SecondaryButton } from "@components/v2/buttons";
import { useOverviewContext } from "@features/overview-v2/hooks";
import { TilesSavingState } from "@features/overview-v2/types";
import {
  type DateInterval,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";
import { TimeGranularityV2Select } from "@shared/selects/time-granularity-v2-select";

import { CompareDateRangeMenu } from "./compare-date-range-menu";
import { DateFilters } from "./date-filters";

const DATE_FORMAT = "MMM dd, yyyy";

function formatDateRange({ startDate, endDate }: DateInterval) {
  const start = parseISO(startDate);
  const end = parseISO(endDate);

  if (isEqual(start, end)) {
    return format(start, DATE_FORMAT);
  } else {
    return `${format(start, DATE_FORMAT)} – ${format(end, DATE_FORMAT)}`;
  }
}

export interface FiltersBarProps {
  readonly onOpenNewTileModal: () => void;
}

export const FiltersBar = ({ onOpenNewTileModal }: FiltersBarProps) => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const [isConfirming, setIsConfirming] = useState(false);

  const {
    filters: {
      dateRange,
      attributionModel,
      attributionWindow,
      accountingMode,
      timeGranularity,
      dateRangeInterval,
      compareDateRangeInterval,
      compareDateRange,
      level,
    },
    saveViewsSavingState: { tilesSavingState },
    lastActionType,
    undoLastAction,
    setTimeGranularity,
    setDateRange,
  } = useOverviewContext();

  const pageContext = {
    dateRange,
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRangeInterval,
    compareDateRange,
    level,
  };

  const compareDateRangeDisplayText = compareDateRangeInterval
    ? formatDateRange(compareDateRangeInterval)
    : "";

  return (
    <div className="px-6 py-4 text-xs flex justify-between">
      <div className="flex shrink-0 gap-3 text-v2-primary">
        <TimeGranularityV2Select
          label="GRANULARITY"
          timeGranularity={timeGranularity}
          hideHourlyOption
          onChange={(value: ITimeGranularityV2) => {
            prettyLogEventPage({
              domain: "Filters Bar",
              action: "onChange",
              additionalActionInfo: "Set Time Granularity",
              data: {
                newTimeGranularity: value,
                pageContext,
              },
            });

            setDateRange({
              startDate: dateRangeInterval.startDate,
              endDate: dateRangeInterval.endDate,
              type: "fixed",
            });
            setTimeGranularity(value);
          }}
          width="92px"
          icon={null}
        />
        <div className="text-xs">
          <div
            className={clsx(
              "text-xs text-v2-primary mb-1",
              "leading-4 tracking-[0.025em] mt-[3px] cursor-default",
            )}
          >
            TIME PERIOD
            <span className="ml-2 text-v2-secondary">
              {formatDateRange(dateRangeInterval)}
            </span>
          </div>
          <DateFilters />
        </div>
        <div>
          <div
            className={clsx(
              "text-xs text-v2-primary mb-1 leading-4",
              "tracking-[0.025em] mt-[3px] cursor-default",
            )}
          >
            TIME COMPARISON
            <span className="ml-2 text-v2-secondary">
              {compareDateRangeDisplayText}
            </span>
          </div>
          <CompareDateRangeMenu className="h-8 w-64" />
        </div>
      </div>
      <div className="flex items-end gap-2">
        {isConfirming ? (
          <span className="text-xs bg-mars-2 px-4 h-8 inline-flex items-center gap-2 rounded-full whitespace-nowrap">
            Remove tile?
            <span>
              <button
                className="font-bold text-mars px-2 h-5 rounded-md hover:bg-v2-primary"
                onClick={() => {
                  undoLastAction();
                  setIsConfirming(false);
                }}
              >
                YES
              </button>
              <button
                className="font-bold text-cosmic px-2 h-5 rounded-md hover:bg-v2-primary"
                onClick={() => setIsConfirming(false)}
              >
                CANCEL
              </button>
            </span>
          </span>
        ) : (
          <SecondaryButton
            disabled={
              lastActionType === null ||
              tilesSavingState === TilesSavingState.SAVING
            }
            className="flex items-center gap-2 font-bold"
            onClick={() => setIsConfirming(!isConfirming)}
          >
            <i className="fa-regular fa-rotate-left text-sm" /> UNDO
          </SecondaryButton>
        )}
        <PrimaryButton
          className="flex items-center justify-center gap-2"
          onClick={() => onOpenNewTileModal()}
        >
          <i className="fa-regular fa-plus" />
          ADD TILE
        </PrimaryButton>
      </div>
    </div>
  );
};
